import { useEffect, useState } from "react";
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { endpoints } from "../../api.js";
import { Icon } from "../../components/Icon.jsx";
import { ConfidenceBar, ErrorBanner, KeyValue, Spinner, StatusPill } from "../../components/ui.jsx";
import { useErrorMessage } from "../../i18n/hooks.js";

function EvidenceItem({ id }) {
  const errMsg = useErrorMessage();
  const [meta, setMeta] = useState(null);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    endpoints.evidence(id).then(
      (d) => alive && setMeta(d.evidence),
      () => alive && setMeta(false),
    );
    return () => {
      alive = false;
    };
  }, [id]);

  const open = async () => {
    setOpening(true);
    setError(null);
    try {
      const { url } = await endpoints.evidenceUrl(id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError(e);
    } finally {
      setOpening(false);
    }
  };

  return (
    <li className="evidence-item">
      <div className="evidence-main">
        <span className="mono small truncate">{meta?.sourceId || id.slice(0, 12)}</span>
        {meta ? (
          <span className="muted small">
            {meta.mime} ·{" "}
            <FormattedNumber value={meta.size / 1024} style="unit" unit="kilobyte" maximumFractionDigits={1} /> ·{" "}
            <FormattedDate value={meta.collectedAt} dateStyle="short" timeStyle="short" />
          </span>
        ) : null}
        {meta?.sha256 ? (
          <span className="mono tiny muted truncate" title={meta.sha256}>
            <FormattedMessage id="evidence.sha256" values={{ hash: meta.sha256 }} />
          </span>
        ) : null}
        {error ? <span className="bad-text small">{errMsg(error)}</span> : null}
      </div>
      <button type="button" className="btn btn-ghost btn-sm" onClick={open} disabled={opening}>
        {opening ? <Spinner size={13} /> : <Icon name="external" size={14} />}
        <FormattedMessage id="evidence.open" />
      </button>
    </li>
  );
}

function FindingDetails({ finding, canReview, onReview, onNote, busy, error }) {
  const errMsg = useErrorMessage();
  return (
    <>
      <div className="props-title">
        <span className={`type-badge ftype-${finding.type}`}>
          <FormattedMessage id={`findingType.${finding.type}`} defaultMessage={finding.type} />
        </span>
        <h3 className="mono break">{finding.value}</h3>
      </div>
      <div className="props-row">
        <span className="props-label">
          <FormattedMessage id="results.col.confidence" />
        </span>
        <ConfidenceBar value={finding.confidence} />
      </div>
      <div className="props-row">
        <span className="props-label">
          <FormattedMessage id="results.col.review" />
        </span>
        <StatusPill ns="review" value={finding.reviewState || "unreviewed"} />
      </div>
      {canReview ? (
        <div className="btn-row">
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={busy || finding.reviewState === "accepted"}
            onClick={() => onReview("accepted")}
          >
            <Icon name="check" size={14} />
            <FormattedMessage id="results.accept" />
          </button>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={busy || finding.reviewState === "rejected"}
            onClick={() => onReview("rejected")}
          >
            <Icon name="x" size={14} />
            <FormattedMessage id="results.reject" />
          </button>
          <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={onNote}>
            <Icon name="note" size={14} />
            <FormattedMessage id="results.addNote" />
          </button>
        </div>
      ) : null}
      <ErrorBanner message={error ? errMsg(error) : null} />
      {finding.note ? (
        <div className="props-block">
          <div className="props-label">
            <FormattedMessage id="results.note" />
          </div>
          <p className="note-text prewrap">{finding.note}</p>
        </div>
      ) : null}
      <div className="props-block">
        <div className="props-label">
          <FormattedMessage id="results.data" />
        </div>
        <KeyValue data={finding.data} />
      </div>
      <div className="props-block">
        <div className="props-label">
          <FormattedMessage id="results.evidence" values={{ count: finding.evidenceIds?.length || 0 }} />
        </div>
        {finding.evidenceIds?.length ? (
          <ul className="evidence-list">
            {finding.evidenceIds.map((id) => (
              <EvidenceItem key={id} id={id} />
            ))}
          </ul>
        ) : (
          <p className="muted small">
            <FormattedMessage id="results.noEvidence" />
          </p>
        )}
      </div>
      <p className="tiny muted">
        <FormattedMessage id="results.collected" values={{ date: <FormattedDate value={finding.createdAt} dateStyle="medium" timeStyle="short" /> }} />
      </p>
    </>
  );
}

function NodeDetails({ node, graph, findings, onSelectNode, onSelectFinding }) {
  const neighbours = graph.edges
    .filter((e) => e.source === node.id || e.target === node.id)
    .map((e) => {
      const otherId = e.source === node.id ? e.target : e.source;
      return { edge: e, other: graph.nodes.find((n) => n.id === otherId), outgoing: e.source === node.id };
    })
    .filter((x) => x.other);
  // The contract has no node→finding link; match on the displayed value.
  const related = findings.filter((f) => f.value === node.label);

  return (
    <>
      <div className="props-title">
        <span className="type-badge">
          <span className={`node-swatch node-${node.type}`} />
          <FormattedMessage id={`nodeKind.${node.type}`} defaultMessage={node.type} />
        </span>
        <h3 className="mono break">{node.label}</h3>
      </div>
      {related.length ? (
        <div className="props-block">
          <div className="props-label">
            <FormattedMessage id="results.relatedFindings" />
          </div>
          <ul className="link-list">
            {related.map((f) => (
              <li key={f.id}>
                <button type="button" className="link-btn" onClick={() => onSelectFinding(f.id)}>
                  <FormattedMessage id={`findingType.${f.type}`} defaultMessage={f.type} />
                  <StatusPill ns="review" value={f.reviewState || "unreviewed"} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="props-block">
        <div className="props-label">
          <FormattedMessage id="results.connections" values={{ count: neighbours.length }} />
        </div>
        <ul className="link-list">
          {neighbours.map(({ edge, other, outgoing }) => (
            <li key={edge.id}>
              <button type="button" className="link-btn" onClick={() => onSelectNode(other.id)}>
                <span className="muted tiny">
                  {outgoing ? "→" : "←"} {edge.kind}
                </span>
                <span className={`node-swatch node-${other.type}`} />
                <span className="mono truncate">{other.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

/** Side/bottom properties + evidence panel for the active finding or graph node. */
export function PropertiesPanel({ active, finding, node, graph, findings, canReview, onReview, onNote, reviewBusy, reviewError, onSelectNode, onSelectFinding }) {
  const intl = useIntl();
  return (
    <aside className="panel props-panel" aria-label={intl.formatMessage({ id: "results.properties" })}>
      <div className="panel-head">
        <Icon name="file" size={15} />
        <FormattedMessage id="results.properties" />
      </div>
      <div className="panel-scroll pad">
        {active?.kind === "finding" && finding ? (
          <FindingDetails
            finding={finding}
            canReview={canReview}
            onReview={(state) => onReview([finding.id], state)}
            onNote={() => onNote([finding.id])}
            busy={reviewBusy}
            error={reviewError}
          />
        ) : active?.kind === "node" && node && graph ? (
          <NodeDetails
            node={node}
            graph={graph}
            findings={findings}
            onSelectNode={onSelectNode}
            onSelectFinding={onSelectFinding}
          />
        ) : (
          <div className="props-empty muted small">
            <Icon name="target" size={22} />
            <FormattedMessage id="results.selectHint" />
          </div>
        )}
      </div>
    </aside>
  );
}
